import React, { useMemo } from 'react'
import { DragDropContext, DropResult } from '@hello-pangea/dnd'
import { Box, CircularProgress, Typography } from '@mui/material'
import { Ticket, TicketStatus } from '@shared/types/entities'
import { KanbanColumn } from './KanbanColumn'

interface TicketKanbanBoardProps {
  tickets: Ticket[]
  loading: boolean
  onTicketClick: (ticketId: string) => void
  onStatusChange: (ticketId: string, newStatus: TicketStatus) => Promise<void>
}

const COLUMN_ORDER: TicketStatus[] = ['Open', 'InProgress', 'OnHold', 'WaitingOnThirdParty', 'Resolved', 'Closed']

export const TicketKanbanBoard: React.FC<TicketKanbanBoardProps> = ({
  tickets,
  loading,
  onTicketClick,
  onStatusChange
}) => {
  // Group tickets by status
  const ticketsByStatus = useMemo(() => {
    const grouped: Record<TicketStatus, Ticket[]> = {
      Open: [],
      InProgress: [],
      OnHold: [],
      WaitingOnThirdParty: [],
      Resolved: [],
      Closed: []
    }

    tickets.forEach(ticket => {
      if (grouped[ticket.status]) {
        grouped[ticket.status].push(ticket)
      }
    })

    // Sort by creation date (newest first) within each column
    Object.keys(grouped).forEach(status => {
      grouped[status as TicketStatus].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
    })

    return grouped
  }, [tickets])

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result

    // Dropped outside a droppable area
    if (!destination) {
      return
    }

    // Dropped in same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return
    }

    const newStatus = destination.droppableId as TicketStatus

    // Status changed - call the API
    if (newStatus !== source.droppableId) {
      try {
        await onStatusChange(draggableId, newStatus)
      } catch (error) {
        console.error('Failed to update ticket status:', error)
        // Parent component should handle error display
      }
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    )
  }

  if (tickets.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        flexDirection="column"
        gap={2}
      >
        <Typography variant="h6" color="text.secondary">
          No tickets found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters or create a new ticket
        </Typography>
      </Box>
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 2,
          minHeight: 600,
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            height: 8
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.100'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.400',
            borderRadius: 4
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'grey.600'
          }
        }}
      >
        {COLUMN_ORDER.map(status => (
          <KanbanColumn
            key={status}
            status={status}
            tickets={ticketsByStatus[status]}
            onTicketClick={onTicketClick}
          />
        ))}
      </Box>
    </DragDropContext>
  )
}
